// TeamDetails.tsx
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Confirm,
  Grid,
  Header,
  Icon,
  Image,
  Label,
  Segment,
  Tab,
  TabPane,
  Menu,
} from "semantic-ui-react";
import { useStore } from "../../app/stores/store";
import { useTranslation } from "react-i18next";
import LoadingComponent from "../../app/layout/LoadingComponent";
import TeamRosterPanel from "./TeamRosterPanel";
import TeamStatsPanel from "./TeamStatsPanel";
import TeamLinksPanel from "./TeamLinksPanel";
import TeamChatPanel from "./TeamChatPanel";
import TeamMatchesTab from "./TeamMatchesTab";
import InviteToTeamModal from "./InviteToTeamModal";
import { CreateChallengeModal } from "./CreateChallengeModal";
import ChallengeFromProfileModal from "./ChallengeFromProfileModal";

const mobileTabStyles = `
  @media (max-width: 768px) {
    .ui.secondary.pointing.menu {
      overflow-x: auto !important;
      overflow-y: hidden !important;
      white-space: nowrap !important;
      flex-wrap: nowrap !important;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: thin;
      padding-bottom: 5px;
    }
    .ui.secondary.pointing.menu::-webkit-scrollbar { height: 3px; }
    .ui.secondary.pointing.menu::-webkit-scrollbar-track { background: #f1f1f1; }
    .ui.secondary.pointing.menu::-webkit-scrollbar-thumb { background: #888; }
    .ui.secondary.pointing.menu .item { flex-shrink: 0 !important; display: inline-block !important; }
    .ui.tab { overflow-x: auto !important; width: 100%; box-sizing: border-box; }
  }
`;

if (
  typeof document !== "undefined" &&
  !document.querySelector("#mobile-tab-fix")
) {
  const style = document.createElement("style");
  style.id = "mobile-tab-fix";
  style.textContent = mobileTabStyles;
  document.head.appendChild(style);
}

export default observer(function TeamDetails() {
  const { id } = useParams<{ id: string }>();
  const { teamStore, userStore, commonStore } = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const {
    selectedTeam,
    loadingInitial,
    loadTeam,
    leaveTeam,
    updateTeamStatus,
    submitting,
    myTeams,
  } = teamStore;

  const [confirmLeave, setConfirmLeave] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(
    (location.state as { openInvite?: boolean } | null)?.openInvite ?? false,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Wait for auth to be ready before firing any API calls.
  // Without this guard the component fires loadTeam() before the token
  // is set in axios, causing a 500 on the backend (null userId).
  useEffect(() => {
    if (commonStore.appLoaded && userStore.isLoggedIn && id) {
      loadTeam(id);
      if (teamStore.myTeams.length === 0) teamStore.loadMyTeams();
    }
  }, [commonStore.appLoaded, userStore.isLoggedIn, id]);

  // Separate effect so cleanup only runs on true unmount, not on every
  // re-render. Putting clearSelectedTeam() in the effect above caused
  // React Strict Mode to wipe selectedTeam between the two mount cycles,
  // leaving the component stuck on the loading screen.
  useEffect(() => {
    return () => teamStore.clearSelectedTeam();
  }, []);

  // Wait until auth state is known
  if (!commonStore.appLoaded) return <LoadingComponent content={t("teams.loading")} />;

  // Not logged in — deep-link guard in App.tsx will redirect to /login
  if (!userStore.isLoggedIn) return null;

  if (loadingInitial || !selectedTeam)
    return <LoadingComponent content={t("teams.loading")} />;

  const team = selectedTeam;
  const isLoggedIn = !!userStore.user;

  const statusColor =
    team.status === "Active"
      ? "green"
      : team.status === "Inactive"
        ? "grey"
        : "red";

  const targetIsMyTeam = myTeams.some((mt) => mt.id === team.id);

  const canChallenge =
    isLoggedIn &&
    !team.isCurrentUserMember &&
    !targetIsMyTeam &&
    team.status === "Active" &&
    myTeams.some(
      (mt) =>
        mt.status === "Active" &&
        mt.sportID === team.sportID &&
        mt.id !== team.id &&
        mt.isCurrentUserCaptain,
    );

  const panes = [
    {
      menuItem: { key: "roster", content: t("teams.tabs.roster"), icon: "users" },
      render: () => (
        <TabPane style={{ overflowX: "auto" }}>
          <TeamRosterPanel team={team} />
        </TabPane>
      ),
    },
    {
      menuItem: { key: "stats", content: t("teams.tabs.stats"), icon: "chart bar" },
      render: () => (
        <TabPane style={{ overflowX: "auto" }}>
          <TeamStatsPanel stats={team.stats} />
        </TabPane>
      ),
    },
    {
      menuItem: { key: "chat", content: t("teams.tabs.chat"), icon: "comments" },
      render: () => (
        <TabPane style={{ overflowX: "auto" }}>
          <TeamChatPanel
            contextType="Team"
            contextId={team.id}
            isMember={team.isCurrentUserMember}
          />
        </TabPane>
      ),
    },
    {
      menuItem: { key: "duels", content: t("teams.tabs.duels"), icon: "shield alternate" },
      render: () => (
        <TabPane style={{ overflowX: "auto" }}>
          <TeamMatchesTab team={team} />
        </TabPane>
      ),
    },
    {
      menuItem: { key: "links", content: t("teams.tabs.links"), icon: "linkify" },
      render: () => (
        <TabPane style={{ overflowX: "auto" }}>
          <TeamLinksPanel team={team} />
        </TabPane>
      ),
    },
  ];

  const renderMobileTabs = () => (
    <div style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <Menu secondary style={{ overflowX: "auto", whiteSpace: "nowrap", flexWrap: "nowrap" }}>
          {panes.map((pane, idx) => (
            <Menu.Item
              key={idx}
              active={activeTabIndex === idx}
              onClick={() => setActiveTabIndex(idx)}
              style={{ flexShrink: 0 }}
            >
              {pane.menuItem.icon && <Icon name={pane.menuItem.icon as any} />}
              {pane.menuItem.content}
            </Menu.Item>
          ))}
        </Menu>
      </div>
      <div style={{ overflowX: "auto" }}>{panes[activeTabIndex]?.render()}</div>
    </div>
  );

  return (
    <Grid stackable style={{ overflowX: "hidden" }}>
      <Grid.Column width={16} style={{ overflowX: "hidden" }}>

        <div style={{ marginBottom: 10 }}>
          <Button basic size="mini" as={Link} to="/teams" style={{ borderRadius: 20 }}>
            <Icon name="chevron left" />
            {t("teamDetails.backToTeams")}
          </Button>
        </div>

        <Segment>
          <Grid stackable>
            <Grid.Column mobile={16} tablet={4} computer={3} textAlign="center">
              <Image
                src={team.logoUrl || "/assets/team-placeholder.png"}
                size="small"
                circular
                centered
                style={{ width: 120, height: 120, objectFit: "cover", border: "3px solid #e0e0e0" }}
              />
              {team.isCurrentUserCaptain && (
                <div style={{ marginTop: 10 }}>
                  <Button as={Link} to={`/teams/${team.id}/edit`} size="mini" basic fluid>
                    <Icon name="edit" />
                    {t("teams.editTeam")}
                  </Button>
                </div>
              )}
            </Grid.Column>

            <Grid.Column mobile={16} tablet={12} computer={13}>
              <Header as="h2" style={{ marginBottom: 6, wordBreak: "break-word" }}>
                {team.name}
                <Label color={statusColor as any} style={{ marginLeft: 10, verticalAlign: "middle" }}>
                  {t(`teams.status.${team.status.toLowerCase()}`)}
                </Label>
              </Header>

              <p style={{ color: "#666", marginBottom: 6 }}>
                <Icon name="futbol" />
                {team.sportName}
                <span style={{ marginLeft: 16 }}>
                  <Icon name="user" />
                  {team.teamMembers.length} {t("teams.members")}
                </span>
                {team.createdAt && (
                  <span style={{ marginLeft: 16 }}>
                    <Icon name="calendar" />
                    {new Date(team.createdAt).toLocaleDateString()}
                  </span>
                )}
              </p>

              {team.description && (
                <p style={{ color: "#444", marginBottom: 12, wordBreak: "break-word" }}>
                  {team.description}
                </p>
              )}

              {team.adminPhone && (
                <p style={{ color: "#666", marginBottom: 12 }}>
                  <Icon name="phone" />
                  <a href={`tel:${team.adminPhone}`} style={{ color: "#666", marginRight: 12 }}>
                    {team.adminPhone}
                  </a>
                  <button
                    onClick={() => window.open(`https://wa.me/${team.adminPhone!.replace(/\D/g, "")}`, "_blank")}
                    title="WhatsApp"
                    style={{
                      backgroundColor: "#25D366", color: "white", border: "none",
                      borderRadius: 6, padding: 8, cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)", display: "inline-flex",
                      alignItems: "center", justifyContent: "center",
                      width: 36, height: 36, marginRight: 6,
                    }}
                  >
                    <Icon name="whatsapp" size="large" style={{ margin: 0 }} />
                  </button>
                  <button
                    onClick={() => window.open(`viber://chat?number=${team.adminPhone!.replace(/\D/g, "")}`, "_blank")}
                    title="Viber"
                    style={{
                      backgroundColor: "#7360F2", color: "white", border: "none",
                      borderRadius: 6, padding: 8, cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)", display: "inline-flex",
                      alignItems: "center", justifyContent: "center",
                      width: 36, height: 36,
                    }}
                  >
                    <Icon name="telegram plane" size="large" style={{ margin: 0 }} />
                  </button>
                </p>
              )}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {team.isCurrentUserCaptain && team.status === "Active" && (
                  <InviteToTeamModal
                    teamId={team.id}
                    externalOpen={inviteOpen}
                    onExternalClose={() => setInviteOpen(false)}
                  />
                )}
                {team.isCurrentUserCaptain && team.status === "Active" && (
                  <CreateChallengeModal teamId={team.id} sportId={team.sportID} />
                )}
                {team.isCurrentUserMember && !team.isCurrentUserCaptain && (
                  <Button size="small" basic color="red" onClick={() => setConfirmLeave(true)} loading={submitting}>
                    <Icon name="sign-out" />{t("teams.leaveTeam")}
                  </Button>
                )}
                {team.isCurrentUserCaptain && team.status === "Active" && (
                  <Button size="small" basic color="grey" loading={submitting}
                    onClick={() => updateTeamStatus(team.id, "Inactive")}>
                    <Icon name="pause" />{t("teams.deactivate")}
                  </Button>
                )}
                {team.isCurrentUserCaptain && team.status === "Inactive" && (
                  <Button size="small" basic color="green" loading={submitting}
                    onClick={() => updateTeamStatus(team.id, "Active")}>
                    <Icon name="play" />{t("teams.activate")}
                  </Button>
                )}
                {canChallenge && (
                  <ChallengeFromProfileModal
                    targetTeamId={team.id}
                    targetTeamName={team.name}
                    targetTeamLogoUrl={team.logoUrl}
                    targetSportId={team.sportID}
                  />
                )}
              </div>
            </Grid.Column>
          </Grid>
        </Segment>

        {isMobile ? renderMobileTabs() : (
          <Tab panes={panes} menu={{ secondary: true, pointing: true }} style={{ marginTop: 16 }} />
        )}

        <Confirm
          open={confirmLeave}
          header={t("teams.leaveTeam")}
          content={t("teams.confirmLeave")}
          cancelButton={t("teams.cancel")}
          confirmButton={<Button negative content={t("teams.leaveTeam")} />}
          onCancel={() => setConfirmLeave(false)}
          onConfirm={async () => {
            setConfirmLeave(false);
            const ok = await leaveTeam(team.id);
            if (ok) navigate("/teams");
          }}
        />
      </Grid.Column>
    </Grid>
  );
});